import type { Language } from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  RadioBigCard,
  RadioGroup,
  Separator,
  Text,
  Tooltip
} from "@prismicio/editor-ui"
import { useState } from "react"

import { LocaleSelection } from "../../../app/components/Locales/LocaleSelection"
import { aiTranslationFlag } from "../../../app/experiments"
import { useRole } from "../../../app/services/user"
import { useDocument } from "../../DocumentProvider"
import { useLocales } from "./useLocales"

export function Locales() {
  const { type } = useDocument()

  const {
    onLocaleChange,
    isEnabled,
    locale,
    langs,
    onCopyToLocale,
    onTranslateToLocale,
    cancelTranslation,
    error,
    clearError
  } = useLocales()

  const [dialogOpen, setDialogOpen] = useState(false)

  function onOpenChange(open: boolean) {
    if (!open) setDialogOpen(open)
  }

  useOnChange(dialogOpen, (open) => {
    if (!open) cancelTranslation()
  })

  const { isContributor } = useRole(locale)
  const canCopyToLocale = !isContributor

  const copyButtonDisabled = type === "new" || !canCopyToLocale

  if (!locale) return null

  if (langs.length < 2) return null

  return (
    <Box flexShrink={0} flexDirection="column">
      <Box
        padding={{ bottom: 6 }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Text variant="smallBold" color="grey9" align="end">
          Locales
        </Text>
        <Tooltip
          visible={!canCopyToLocale}
          title={"You can't make changes"}
          content="Your role is read-only. Only users with editing permission can make changes."
        >
          <Button
            invisible
            size="small"
            onClick={() => setDialogOpen(true)}
            disabled={copyButtonDisabled}
            underline
          >
            Copy to another locale
          </Button>
        </Tooltip>
      </Box>
      <CopyToLocaleDialog
        open={dialogOpen}
        onOpenChange={onOpenChange}
        locale={locale}
        langs={langs}
        isEnabled={isEnabled}
        onCopyToLocale={onCopyToLocale}
        onTranslateToLocale={onTranslateToLocale}
        error={error}
        clearError={clearError}
      />
      <LocaleSelection
        locale={locale}
        isEnabled={isEnabled}
        onLocaleChange={onLocaleChange}
        langs={langs}
      />

      <Separator sx={{ marginBlock: 24 }} />
    </Box>
  )
}

interface CopyToLocaleProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locale: string
  onCopyToLocale: (localeId: string) => void
  onTranslateToLocale: (localeId: string) => void
  langs: Language[]
  isEnabled: boolean
  error?: { title: string; description: string }
  clearError: () => void
}

function CopyToLocaleDialog(props: CopyToLocaleProps) {
  const {
    open,
    onOpenChange,
    locale,
    onCopyToLocale,
    onTranslateToLocale,
    langs,
    isEnabled,
    error,
    clearError
  } = props

  const [mode, setMode] = useState<CopyToLocaleMode>(copyToLocaleModes[0])
  const [dialogLocale, setDialogLocale] = useState<string>(locale)

  useOnChange(open, (open) => {
    if (!open) {
      setMode(copyToLocaleModes[0])
      setDialogLocale(locale)
    }
  })

  function onModeChange(mode: CopyToLocaleMode) {
    setMode(mode)
    clearError()
  }

  function onLocaleChange(localeId: string) {
    setDialogLocale(localeId)
    clearError()
  }

  // Evaluating a flag also produces an analytics event as a side effect.
  // In dialogs, we only want this to happen once it is open.
  const translationsEnabled = open && aiTranslationFlag() === "on"

  const okButtonTitle = translationsEnabled
    ? okButtonTitles[mode]
    : copyToLocaleTitles[mode]

  let okButtonOnClick: (localeId: string) => void
  switch (mode) {
    case "copy":
      okButtonOnClick = onCopyToLocale
      break
    case "translate":
      okButtonOnClick = onTranslateToLocale
      break
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="small">
      <DialogHeader icon="language" title="Copy to another locale" />
      <DialogContent>
        <Box
          overflowY="auto"
          padding={{ inline: 16, top: 16, bottom: 8 }}
          flexDirection="column"
          gap={16}
        >
          {error && (
            <Alert
              color="error"
              noShadow
              title={error.title}
              subtitle={error.description}
              footerButtons={{
                action: {
                  text: "Try again",
                  onClick: () => {
                    okButtonOnClick(dialogLocale)
                  }
                }
              }}
            />
          )}
          {translationsEnabled && (
            <>
              <RadioGroup
                disabled={!isEnabled}
                values={copyToLocaleModes}
                selectedValue={mode}
                evenItemSize
                renderItem={(value) => <RadioGroupCard value={value} />}
                onChange={onModeChange}
              />
              <Separator style="dashed" />
            </>
          )}
          <Box flexDirection="column" gap={4}>
            <Text variant="emphasized" color="grey12">
              Target language
            </Text>
            <LocaleSelection
              isEnabled={isEnabled}
              locale={dialogLocale}
              onLocaleChange={onLocaleChange}
              langs={langs}
            />
          </Box>
        </Box>
        <DialogActions
          ok={{
            text: okButtonTitle,
            onClick: () => okButtonOnClick(dialogLocale),
            loading: !isEnabled,
            disabled: mode === "translate" && dialogLocale === locale
          }}
          cancel={{ text: "Cancel" }}
        />
      </DialogContent>
    </Dialog>
  )
}

const copyToLocaleModes = ["copy", "translate"] as const

type CopyToLocaleMode = typeof copyToLocaleModes[number]

const copyToLocaleTitles = {
  copy: "Copy all content",
  translate: "Copy and translate"
}

const okButtonTitles = {
  copy: "Copy all content",
  translate: "Translate content"
}

interface RadioGroupCardProps {
  value: CopyToLocaleMode
}

function RadioGroupCard(props: RadioGroupCardProps) {
  const { value } = props
  switch (value) {
    case "copy":
      return (
        <RadioBigCard
          icon="contentCopy"
          title="Copy all content"
          description="Create a new locale version by copying all the content from this page. Ideal for manual translation and adjustments."
        />
      )
    case "translate":
      return (
        <RadioBigCard
          icon="translate"
          title="Translate with AI"
          badge={{
            icon: "autoFixHigh",
            title: "New"
          }}
          description="Automatically translate all the content of this page into the selected language. Perfect for saving time and quickly obtaining a translation base."
        />
      )
  }
}
